import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import { Search, RefreshCw, ExternalLink, FileText, AlertTriangle } from 'lucide-react';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000' });
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const IMPACT_COLORS = {
  HIGH:   { bg: '#FEE2E2', text: '#991B1B', accent: '#DC2626' },
  MEDIUM: { bg: '#FFFBEB', text: '#92400E', accent: '#D97706' },
  LOW:    { bg: '#F0FDF4', text: '#166534', accent: '#16A34A' },
  NONE:   { bg: '#F3F4F6', text: '#374151', accent: '#D1D5DB' },
};

const STATUS_COLORS = {
  PENDING:     { bg: '#EFF6FF', text: '#1E40AF' },
  IN_PROGRESS: { bg: '#FFFBEB', text: '#92400E' },
  ACTIONED:    { bg: '#F0FDF4', text: '#166534' },
  DISMISSED:   { bg: '#F3F4F6', text: '#374151' },
};

const SOURCE_LABELS = {
  CSSF_WEBSITE: 'CSSF',
  ESMA:         'ESMA',
  EBA:          'EBA',
  LEGILUX:      'Legilux',
};

const Badge = ({ label, bg, text }) => (
  <span style={{
    background: bg, color: text,
    padding: '2px 9px', borderRadius: 99,
    fontSize: 10, fontWeight: 700,
    whiteSpace: 'nowrap', display: 'inline-block',
  }}>
    {label}
  </span>
);

const borderColor = (level, theme) => IMPACT_COLORS[level]?.accent || theme.border;

const ImpactAssessments = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { theme } = useTheme();

  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filterLevel, setFilterLevel] = useState('HIGH_MEDIUM');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterFund, setFilterFund] = useState('ALL');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await API.get('/regulatory/impact-assessments', { params: { limit: 2000 } });
      setAssessments(res.data.assessments || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load assessments');
    } finally { setLoading(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  useEffect(() => { load(); }, []);

  const funds = ['ALL', ...new Set(assessments.map(a => a.fund_name).filter(Boolean))];

  const filtered = assessments.filter(a => {
    if (filterLevel === 'HIGH_MEDIUM' && !['HIGH', 'MEDIUM'].includes(a.impact_level)) return false;
    if (filterLevel !== 'ALL' && filterLevel !== 'HIGH_MEDIUM' && a.impact_level !== filterLevel) return false;
    if (filterStatus !== 'ALL' && a.status !== filterStatus) return false;
    if (filterFund !== 'ALL' && a.fund_name !== filterFund) return false;
    if (search && !a.document_title?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const highCount = filtered.filter(a => a.impact_level === 'HIGH').length;
  const mediumCount = filtered.filter(a => a.impact_level === 'MEDIUM').length;
  const pendingCount = filtered.filter(a => a.status === 'PENDING').length;
  const overdueCount = filtered.filter(a =>
    a.action_deadline && new Date(a.action_deadline) < new Date() && a.status !== 'ACTIONED'
  ).length;

  const inputStyle = {
    padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${theme.border}`, fontSize: 13,
    color: theme.textPrimary, background: theme.bgInput,
    outline: 'none', cursor: 'pointer',
  };

  return (
    <div>
      {/* Back */}
      <button onClick={() => navigate('/')} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: theme.textSecondary, fontWeight: 600, fontSize: 12,
        marginBottom: 16, padding: 0,
      }}>
        Back to Dashboard
      </button>

      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.textPrimary }}>
            Regulatory Impact Assessments
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
            Rule-based impact scoring against your fund portfolio
          </p>
        </div>
        <button onClick={refresh} disabled={refreshing || loading} style={{
          background: refreshing || loading ? theme.bgBadge : '#3B82F6',
          color: refreshing || loading ? theme.textTertiary : 'white',
          border: 'none', borderRadius: 8, padding: '9px 18px',
          fontWeight: 700, fontSize: 13, cursor: refreshing || loading ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', value: filtered.length, color: '#3B82F6' },
          { label: 'High', value: highCount, color: '#DC2626' },
          { label: 'Medium', value: mediumCount, color: '#D97706' },
          { label: 'Pending', value: pendingCount, color: '#16A34A' },
          { label: 'Overdue', value: overdueCount, color: '#8B5CF6' },
        ].map(s => (
          <div key={s.label} style={{
            background: theme.bgCard, borderRadius: 10,
            padding: '16px 20px', border: `1px solid ${theme.border}`,
            boxShadow: theme.shadowSm,
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FileText size={18} color={s.color} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.textPrimary, lineHeight: 1 }}>{s.value}</p>
              <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary, fontWeight: 600 }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Overdue warning */}
      {overdueCount > 0 && (
        <div style={{ background: '#FEF2F2', borderRadius: 8, padding: '12px 16px', marginBottom: 16, border: '1px solid #FECACA', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={15} color="#DC2626" />
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
            {overdueCount} assessment{overdueCount > 1 ? 's' : ''} past deadline - immediate action required
          </p>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: theme.bgCard, borderRadius: 10, padding: '12px 14px', marginBottom: 14, border: `1px solid ${theme.border}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
          <Search size={13} color={theme.textTertiary} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text" placeholder="Search documents..." value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 30, width: '100%', boxSizing: 'border-box', cursor: 'text' }}
          />
        </div>

        <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} style={inputStyle}>
          <option value="HIGH_MEDIUM">High & Medium</option>
          <option value="ALL">All Impact Levels</option>
          <option value="HIGH">High Only</option>
          <option value="MEDIUM">Medium Only</option>
          <option value="LOW">Low Only</option>
        </select>

        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inputStyle}>
          <option value="ALL">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="ACTIONED">Actioned</option>
          <option value="DISMISSED">Dismissed</option>
        </select>

        <select value={filterFund} onChange={e => setFilterFund(e.target.value)} style={{ ...inputStyle, maxWidth: 220 }}>
          {funds.map(f => <option key={f} value={f}>{f === 'ALL' ? 'All Funds' : f}</option>)}
        </select>

        <span style={{ fontSize: 12, color: theme.textTertiary, whiteSpace: 'nowrap' }}>
          {filtered.length} of {assessments.length}
        </span>
      </div>

      {/* Assessment list */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 14 }}>Loading assessments...</div>
      ) : error ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#DC2626', fontSize: 14 }}>{error}</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 14, background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}` }}>
          No assessments found.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(a => {
            const overdue = a.action_deadline &&
              new Date(a.action_deadline) < new Date() &&
              a.status !== 'ACTIONED';
            const impactMeta = IMPACT_COLORS[a.impact_level] || IMPACT_COLORS.NONE;
            const statusMeta = STATUS_COLORS[a.status] || STATUS_COLORS.DISMISSED;

            return (
              <div key={a.id} style={{
                background: theme.bgCard, borderRadius: 10,
                padding: isMobile ? '12px 14px' : '14px 18px',
                border: `1px solid ${theme.border}`,
                borderLeft: `3px solid ${borderColor(a.impact_level, theme)}`,
                boxShadow: theme.shadowSm, transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#3B82F6'}
              onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 5, marginBottom: 8, flexWrap: 'wrap' }}>
                      <Badge label={`${a.impact_level || 'NONE'} IMPACT`} bg={impactMeta.bg} text={impactMeta.text} />
                      <Badge label={a.status?.replace(/_/g, ' ') || 'UNKNOWN'} bg={statusMeta.bg} text={statusMeta.text} />
                      <Badge label={SOURCE_LABELS[a.document_source] || a.document_source} bg="#EFF6FF" text="#1D4ED8" />
                      {a.is_binding && <Badge label="BINDING" bg="#FEE2E2" text="#991B1B" />}
                      {overdue && <Badge label="OVERDUE" bg="#FFFBEB" text="#92400E" />}
                    </div>

                    <a href={a.source_url} target="_blank" rel="noopener noreferrer" style={{
                      color: theme.textPrimary, fontWeight: 600, fontSize: isMobile ? 13 : 14,
                      textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.4,
                    }}
                      onMouseEnter={e => e.currentTarget.style.color = '#3B82F6'}
                      onMouseLeave={e => e.currentTarget.style.color = theme.textPrimary}
                    >
                      {a.document_title}
                      <ExternalLink size={12} style={{ flexShrink: 0, marginTop: 3, opacity: 0.5 }} />
                    </a>

                    <p style={{ margin: '4px 0 0', fontSize: 12, color: theme.textSecondary }}>
                      Fund: <strong style={{ color: theme.textPrimary }}>{a.fund_name}</strong>
                    </p>

                    {a.action_description && !isMobile && (
                      <p style={{ margin: '6px 0 0', fontSize: 12, color: theme.textSecondary, lineHeight: 1.6 }}>
                        {a.action_description.length > 220 ? `${a.action_description.substring(0, 220)}...` : a.action_description}
                      </p>
                    )}
                  </div>

                  <div style={{ textAlign: isMobile ? 'left' : 'right', flexShrink: 0, minWidth: isMobile ? 'auto' : 112 }}>
                    {a.action_deadline ? (
                      <>
                        <p style={{ margin: 0, fontSize: 11, color: overdue ? '#DC2626' : theme.textSecondary, whiteSpace: 'nowrap', fontWeight: overdue ? 700 : 500 }}>
                          {overdue ? 'Deadline passed' : 'Deadline'}
                        </p>
                        <p style={{ margin: '3px 0 0', fontSize: 11, color: overdue ? '#DC2626' : theme.textSecondary, whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {new Date(a.action_deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </>
                    ) : (
                      <p style={{ margin: 0, fontSize: 11, color: theme.textSecondary, whiteSpace: 'nowrap', fontWeight: 500 }}>
                        No deadline
                      </p>
                    )}
                    {!isMobile && (
                      <p style={{ margin: '3px 0 0', fontSize: 10, color: theme.textTertiary }}>
                        {a.document_type?.replace(/_/g, ' ') || 'Document'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ color: theme.textTertiary, fontSize: 12, marginTop: 14, textAlign: 'center' }}>
        Showing {filtered.length} of {assessments.length} assessments
      </p>
    </div>
  );
};

export default ImpactAssessments;